import React, { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import TimeAgo from 'react-timeago';
import { Draggable } from "react-beautiful-dnd";
import { Card } from "react-bootstrap";
import { moveCard } from "../../../actions/crew-kanban";
import { Unit } from "../../../helpers/unit";
import { getSpec } from "../utils";
import { isToday, usePermission } from "../../../helpers/common";
import { dateToUTC } from "../../../helpers/moment-ex";
import { DISPLAY_DATETIME_FORMAT2 } from "../../../constants";
import moment from "moment";

const getItemStyle = (draggableStyle) => ({
    userSelect: 'none',
    ...draggableStyle,
});

const EGCard = props => {
    const { egId, crewId, index, onFilter } = props;
    const dispatch = useDispatch();
    const crews = useSelector((state) => state.crewKanban.crews);
    const timezoneIdentifier = useSelector((state) => state.farm.currentFarm.timezone_identifier);
    const egListById = useSelector((state) => state.crewKanban.egListById);
    const { weightShortName, areaShortName } = useSelector((state) => state.user.settings);
    const range = useSelector((state) => state.analyze.searchParams.range);
    const egReportIssue = useSelector((state) => state.equipmentGroup.EGReportIssue.list);
    const crewList = useSelector((state) => state.crewKanban.crewList);
    const columns = useSelector((state) => state.crewKanban.columns);

    const egItem = useMemo(() => {
        let egItem = egListById.hasOwnProperty(egId) ?
            egListById[egId] : null;
        if (egItem && crews[crewId] && crews[crewId].egPlanById && crews[crewId].egPlanById[egId])
            egItem = { ...egItem, ...crews[crewId].egPlanById[egId] };
        return egItem;
    }, [egListById]);

    const spec = (egItem && egItem.implement && egItem.implement.specification) ? getSpec(egItem.implement.specification) : null;
    const isChanged = egItem && (egItem.crewId !== crewId);
    const allow = usePermission();
    const isAllowEdit = allow("crew", "edit") ? isToday(range) : false
    const unit = Unit.getInstance();
    const today = isToday(range);

    if (!egItem || !onFilter(egItem)) return <></>;

    const onRevert = () => {
        let crewIds = [];
        crewList.forEach((each) => {
            crewIds.push(each.crew_uuid)
        });
        const egList = [];
        for (let x in egListById) {
            if (crewIds.includes(egListById[x].crewId)) {
                egList.push(egListById[x])
            }
        }
        const assigned = [];
        egList.filter((e) => e.crew && e.crew.crew_uuid && e.crew.crew_uuid.length > 0).forEach((each) => {
            if (!columns.map((e) => e.crewId).filter((e) => e).includes(each.crewId)) assigned.push(each.equipment_group_uuid)
        })

        dispatch(moveCard({
            oldCardIndex: index,
            newCardIndex: 0,
            sourceCrewId: crewId,
            destCrewId: assigned.includes(egId) ? "assigned" : egItem.crewId
        }));
    };

    const onUnassign = () => {
        dispatch(moveCard({
            oldCardIndex: index,
            newCardIndex: 0,
            sourceCrewId: crewId,
            destCrewId: "unassigned"
        }));
    };

    const getEGImageURL = (data, egId) => {
        let egIssue = egReportIssue && egReportIssue[egId] ? egReportIssue[egId] : null;
        return egIssue && !egIssue.is_resolve ? data.img.ic.replace("/ic/0", "/ic/3") : data.img.ic;
    }

    const showEGReportIcon = (egId) => {
        let egIssue = egReportIssue && egReportIssue[egId] ? egReportIssue[egId] : null;
        return egIssue && !egIssue.is_resolve ?
            <i className="fa fa-exclamation-triangle ml-3 mt-2" style={{ color: "red" }} title={egIssue.issue} />
            : <div></div>
    }

    return <Draggable draggableId={egId} index={index} isDragDisabled={!isAllowEdit}>
        {(provided, snapshot) => (
            <div ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                style={getItemStyle(provided.draggableProps.style)}>
                <Card className={`kanban-card ${today && isChanged ? "changed" : ""} ${snapshot.isDragging ? "dragging" : ""}`}>
                    <Card.Body>
                        <div className="card-caption d-flex flex-row">
                            <Card.Img src={getEGImageURL(egItem, egItem.equipment_group_uuid)} />
                            <div className="card-title-wrap">
                                <Card.Title className="text-muted" title={egItem.equipment_group_name}>
                                    {egItem.equipment_group_name}{egItem.primary_category_name && ` | ${egItem.primary_category_name}`}
                                </Card.Title>
                                {!egItem.vehicle.is_truck && (
                                    <Card.Subtitle className="mb-2 text-muted">
                                        {egItem.vehicle.name}
                                        {egItem.vehicle.name && egItem.implement.name && <i className="fa fa-chain" />}
                                        {egItem.implement.name}
                                    </Card.Subtitle>
                                )}
                            </div>
                            <span>
                                {
                                    showEGReportIcon(egItem.equipment_group_uuid)
                                }
                            </span>
                        </div>
                        {egItem.operator.name &&
                            <Card.Text className="mb-2">
                                <i className="fa fa-user mr-1" /> {egItem.operator.name}
                            </Card.Text>}
                        {spec && spec.hasOwnProperty("width") && <Card.Text className="mb-1">
                            <b>Width</b> : {spec.width}
                        </Card.Text>}
                        {spec && spec.hasOwnProperty("capacity") && <Card.Text className="mb-1">
                            <b>Capacity</b> : {unit.toRoundOfWeight(spec.capacity)} {weightShortName}
                        </Card.Text>}
                        {egItem.hasOwnProperty("acres_per_day") && egItem.acres_per_day !== null &&
                            <Card.Text className="mb-2">
                                <b>{areaShortName}/Day</b> : {unit.toRoundOfArea(egItem.acres_per_day)}
                            </Card.Text>}
                        <div className="d-flex justify-content-between">
                            <div>
                                {today && <Card.Link className="leave-crew" href="#" onClick={onUnassign} title="Move to unassigned equipment group list">Leave Crew</Card.Link>}
                            </div>
                            <div>
                                {today && isChanged && <Card.Link href="#" className="float-right" title="Move to original state"
                                    onClick={onRevert}>Revert</Card.Link>}
                                {!isChanged && <Card.Link className="timeline float-right">
                                    <TimeAgo date={dateToUTC(egItem.crew.start_date, timezoneIdentifier)}
                                        title={moment.utc(egItem.crew.start_date).format(DISPLAY_DATETIME_FORMAT2)}
                                        minPeriod={10} />
                                </Card.Link>}
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            </div>)}
    </Draggable>;
};

export default EGCard;